import {
  addTransferOrderNote,
  removeTransferOrderNote,
  setTransferOrderNotes,
  updateTransferOrderNote,
} from "../slice/orderSlice";
import {
  addPurchaseOrderNote,
  removePurchaseOrderNote,
  setPurchaseOrderNotes,
  updatePurchaseOrderNote,
} from "../slice/purchaseOrderSlice";
import {
  addConsignmentNote,
  removeConsignmentNote,
  setConsignmentNotes,
  updateConsignmentNote,
} from "../slice/shipmentSlice";
import { apiSlice } from "./apiSlice";

const NOTE_STATE = {
  shipment: {
    slice: "shipment",
    key: "consignment",
    add: addConsignmentNote,
    update: updateConsignmentNote,
    remove: removeConsignmentNote,
    set: setConsignmentNotes,
  },
  purchase: {
    slice: "purchaseOrder",
    key: "purchaseOrder",
    add: addPurchaseOrderNote,
    update: updatePurchaseOrderNote,
    remove: removePurchaseOrderNote,
    set: setPurchaseOrderNotes,
  },
  transfer: {
    slice: "order",
    key: "transferOrder",
    add: addTransferOrderNote,
    update: updateTransferOrderNote,
    remove: removeTransferOrderNote,
    set: setTransferOrderNotes,
  },
};

const LIST_CACHE = {
  shipment: {
    tags: ["consignmentList", "filteredConsignmentList"],
    endpoints: ["getConsignments", "getFilterConsignmentsByRule"],
  },
  purchase: {
    tags: ["purchaseOrderList"],
    endpoints: ["getPurchaseOrders"],
  },
};

/**
 * Maps API and UI type names to the notes store key.
 * @param {string} orderType Module or page type for the note parent.
 * @return {string} Normalized notes store key.
 */
export const resolveNoteOrderType = orderType => {
  if (orderType === "purchase" || orderType === "purchase_order") return "purchase";
  if (orderType === "transfer" || orderType === "transfer_order") return "transfer";
  return "shipment";
};

/**
 * Reads the current notes array for an order type.
 * @param {Object} state Redux root state.
 * @param {string} orderType Module or page type for the note parent.
 * @return {Array<Object>} Current notes, or an empty array.
 */
export const getNotesFromState = (state, orderType) => {
  const config = NOTE_STATE[resolveNoteOrderType(orderType)];
  const notes = state[config.slice]?.[config.key]?.notes;
  return Array.isArray(notes) ? notes : [];
};

/**
 * Appends a created note to the matching notes store.
 * @param {Function} dispatch Redux dispatch.
 * @param {string} orderType Module or page type for the note parent.
 * @param {Object} note Created note payload.
 * @return {void}
 */
export const dispatchAddNote = (dispatch, orderType, note) => {
  dispatch(NOTE_STATE[resolveNoteOrderType(orderType)].add(note));
};

/**
 * Merges note fields into the matching notes store.
 * @param {Function} dispatch Redux dispatch.
 * @param {string} orderType Module or page type for the note parent.
 * @param {Object} note Note fields including id.
 * @return {void}
 */
export const dispatchUpdateNote = (dispatch, orderType, note) => {
  dispatch(NOTE_STATE[resolveNoteOrderType(orderType)].update(note));
};

/**
 * Removes a note from the matching notes store.
 * @param {Function} dispatch Redux dispatch.
 * @param {string} orderType Module or page type for the note parent.
 * @param {number} id Note id.
 * @return {void}
 */
export const dispatchRemoveNote = (dispatch, orderType, id) => {
  dispatch(NOTE_STATE[resolveNoteOrderType(orderType)].remove(id));
};

/**
 * Replaces the notes array after a failed optimistic update.
 * @param {Function} dispatch Redux dispatch.
 * @param {string} orderType Module or page type for the note parent.
 * @param {Array<Object>} notes Notes to restore.
 * @return {void}
 */
export const dispatchSetNotes = (dispatch, orderType, notes) => {
  dispatch(NOTE_STATE[resolveNoteOrderType(orderType)].set(notes));
};

/**
 * Updates the notes icon on cached shipment list pages without refetching.
 * @param {Function} dispatch Redux dispatch.
 * @param {Function} getState Redux state accessor.
 * @param {number} orderId Parent consignment id.
 * @param {boolean} isNoteAvailable Whether the consignment still has notes.
 * @return {Function} Restores the previous icon state.
 */
export const patchNoteAvailability = (dispatch, getState, orderId, isNoteAvailable) =>
  patchRecordListFields(dispatch, getState, orderId, "shipment", record => {
    record.is_note_available = isNoteAvailable;
  });

/**
 * Patches cached board/list rows after a note delete without refetching.
 * @param {Function} dispatch Redux dispatch.
 * @param {Function} getState Redux state accessor.
 * @param {number} orderId Parent record id.
 * @param {string} orderType Module or page type for the note parent.
 * @param {{ remainingCount: number, wasOpenTask: boolean }} options Delete context.
 * @return {Function} Restores the previous list row fields.
 */
export const patchRecordAfterNoteDelete = (
  dispatch,
  getState,
  orderId,
  orderType,
  { remainingCount, wasOpenTask },
) =>
  patchRecordListFields(dispatch, getState, orderId, orderType, record => {
    if (remainingCount === 1) record.is_note_available = false;
    if (wasOpenTask && record.open_task_count != null) {
      record.open_task_count = Math.max(0, Number(record.open_task_count) - 1);
    }
  });

const patchRecordListFields = (dispatch, getState, orderId, orderType, applyPatch) => {
  const config = LIST_CACHE[resolveNoteOrderType(orderType)];
  if (!config || orderId == null) return () => {};
  const restores = [];
  apiSlice.util.selectInvalidatedBy(getState(), config.tags).forEach(({ endpointName, originalArgs }) => {
    if (!config.endpoints.includes(endpointName)) return;
    let snapshot;
    dispatch(
      apiSlice.util.updateQueryData(endpointName, originalArgs, draft => {
        const record = draft.results?.find(item => item.id === orderId);
        if (!record) return;
        snapshot = {
          is_note_available: record.is_note_available,
          open_task_count: record.open_task_count,
        };
        applyPatch(record);
      }),
    );
    if (snapshot) {
      restores.push(() =>
        dispatch(
          apiSlice.util.updateQueryData(endpointName, originalArgs, draft => {
            const record = draft.results?.find(item => item.id === orderId);
            if (!record) return;
            record.is_note_available = snapshot.is_note_available;
            record.open_task_count = snapshot.open_task_count;
          }),
        ),
      );
    }
  });
  return () => restores.forEach(restore => restore());
};
